import { useLocation } from 'react-router-dom';
import { useDynamicCatalog } from '../hooks/useDynamicCatalog';
import { useAuthContext } from '../context/AuthContext';
import { useFeatureFlagsContext } from '../context/FeatureFlagContext';
import { useLanguage } from '../hooks/useLanguage';
import {
  useSchemaCounts, useDataCounts, useJourneyCounts, useTemplateCounts, useConfigurationCounts,
  useLearningCounts
} from '../hooks/useSchemaCounts';
import { isFeatureEnabled } from '../utils/featureGate';
import { useSourcingAccess } from '../hooks/useSourcingAccess.js';
import { meetsRequirement } from '../config/plans';

// 🏛️ Atomic Snippets
import { NavigationDrawer } from '../components/snippets/navigation-drawer.snippet.jsx';
import { mayCreateProjects } from '@moksy/common/src/permissions/reviewerSeparation.js';

// A child that names its own `roles` is shown only to them; one that does not
// inherits its group's. Operator groups resolve every child from the desk
// table (useDynamicCatalog's BY_DESK), so without this a group visible to
// Support for one row — Enquiries — would open onto the whole marketplace
// console. A group left with no rows is dropped rather than shown empty.
export const pruneChildren = (item, role) => {
  if (!Array.isArray(item.children)) return item;
  const children = item.children
    .filter((child) => !Array.isArray(child.roles) || child.roles.includes(role))
    .map((child) => pruneChildren(child, role))
    .filter(Boolean);
  return children.length ? { ...item, children } : null;
};

export default function Sidebar({ isOpen, onClose, isCollapsed, onToggleCollapse }) {
  const location = useLocation();
  const catalog = useDynamicCatalog();
  const { user, activeRole, availableRoles, loading, personaLimits } = useAuthContext();
  const { flags } = useFeatureFlagsContext();
  const brand = useLanguage('branding');

  const primaryRole = user?.role || null;
  const safeActiveRole = Array.isArray(availableRoles) && availableRoles.includes(activeRole)
    ? String(activeRole || '').toLowerCase()
    : primaryRole;
  const effectiveRole = safeActiveRole || primaryRole || null;

  // Owner-group operators aren't customers — subscription tier gates don't
  // apply to them (they never carry a plan). Everyone else is gated by tier.
  const isOwner = String(user?.group || '').toLowerCase() === 'owner';
  // The job pool is for the Moksy team only (staff or a sourcing-team roster).
  const onSourcingTeam = useSourcingAccess(user?.userId || user?.id || null);

  const menu = !loading && user ? catalog.filter((item) => {
    if (!Array.isArray(item.roles)) return false;

    // All other items: filter by the currently active role context
    const hasRole = effectiveRole ? item.roles.includes(effectiveRole) : false;
    const isEnabled = item.feature ? isFeatureEnabled(flags, item.feature) : true;
    // Tier gate: paid-only surfaces (e.g. Pillars, Concierge) stay hidden for
    // free customers so their sidebar shows only what they need + have.
    const meetsTier = !item.minTier || isOwner || meetsRequirement(user, item.minTier);
    const meetsSourcing = !item.sourcingTeam || onSourcingTeam;
    return hasRole && isEnabled && meetsTier && meetsSourcing;
  }).map((item) => pruneChildren(item, effectiveRole)).filter(Boolean) : [];

  // The schema categories carry live counts. Fetched only when the section is
  // actually in this account's menu — the listing behind it is admin-only, so
  // asking for it from a customer's sidebar would be a 403 on every page load.
  const showsSchemas = menu.some((item) => item.id === 'op-schemas');
  const showsData = menu.some((item) => item.id === 'op-knowledge-base');
  const showsJourneys = menu.some((item) => item.id === 'op-journey');
  const schemaCounts = useSchemaCounts(showsSchemas);
  const dataCounts = useDataCounts(showsData);
  const journeyCounts = useJourneyCounts(showsJourneys);
  const showsTemplates = menu.some((item) => item.id === 'op-reqv2-templates');
  const templateCounts = useTemplateCounts(showsTemplates);
  const showsConfig = menu.some((item) => item.id === 'op-configurations');
  const configCounts = useConfigurationCounts(showsConfig);
  const showsLearning = menu.some((item) => item.id === 'op-learning');
  const learningCounts = useLearningCounts(showsLearning);

  // One decoration for both sections: a row carries a `countKey`, and the
  // section it belongs to says which map to read it from. Two near-identical
  // blocks was how the second one came to be subtly different.
  const fill = (item, counts) => (!counts ? item : {
    ...item,
    children: (item.children || []).map((child) => (
      child.countKey && Number.isFinite(counts[child.countKey])
        ? { ...child, count: counts[child.countKey] }
        : child
    ))
  });
  const decorated = menu.map((item) => {
    if (item.id === 'op-schemas') return fill(item, schemaCounts);
    if (item.id === 'op-knowledge-base') return fill(item, dataCounts);
    if (item.id === 'op-journey') return fill(item, journeyCounts);
    if (item.id === 'op-reqv2-templates') return fill(item, templateCounts);
    if (item.id === 'op-configurations') return fill(item, configCounts);
    if (item.id === 'op-learning') return fill(item, learningCounts);
    return item;
  });

  // Reviewers do not start projects — the services refuse it, so offering the
  // button would be a promise the platform breaks. See
  // packages/common/src/permissions/reviewerSeparation.js for why the rule
  // exists; this only stops the UI advertising what the API will reject.
  //
  // Asked of the HAT, not the account. An operator who also reviews is exempt
  // as a person, but while they are wearing the reviewer hat the button would
  // contradict the console they are looking at. `personaLimits` answers for
  // the current persona; the direct call is the fallback for any consumer
  // rendering this outside the persona-aware context (the older tests do).
  const canStartProjects = personaLimits
    ? personaLimits.canCreateProjects
    : mayCreateProjects({
      role: primaryRole,
      assignedRoles: Array.isArray(availableRoles) ? availableRoles : []
    }).allowed;

  return (
    <NavigationDrawer
      isOpen={isOpen}
      onClose={onClose}
      brand={brand}
      menu={decorated}
      canStartProjects={canStartProjects}
      currentPath={location.pathname + location.search}
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
    />
  );
}
